import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { gymAPI } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { RabelProvider, RabelToggle, useRabel, Burst, Flames, sassLine, RABEL_CSS } from './gymRabel';
import GymProgress from './gymProgress';
import { GymHome, CategoryPage, CategoryPicker, AddFromSession, AddToCalendar } from './gymHome';
import { CheckIn } from './gymCheckin';
import Rehab from './rehab';
import { WeightInput, UnitSwitch, unitOf, otherUnit, fmtWeight, fromKg, toKg, plain, weightStep } from './gymUnits';

/**
 * The gym: a private microsite at /gym for Simon and Rachel, made to live on a
 * phone's home screen. Sessions are plans (exercises, each a list of sets of
 * reps, weight and rest); following one ticks sets, and each tick starts that
 * set's rest timer, which counts down and stops at zero.
 *
 * Everything is saved as it changes, so a phone that locks or reloads picks up
 * the workout where it was, rest timer included (it runs off a clock time, not
 * a counter).
 */

export const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const setKey = (ex, s) => `${ex.id}/${s.id}`;

function fmtClock(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
function fmtRest(seconds) {
  if (!seconds) return 'no rest';
  return seconds % 60 === 0 ? `${seconds / 60} min` : seconds > 60 ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : `${seconds}s`;
}
export function ago(iso) {
  if (!iso) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 14) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}
/** Same as the server's: "Pec fly" and "pec  Fly " are one machine. */
const exerciseKey = (name) => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();
const stem = (w) => w.replace(/(es|s)$/, '');
/** Known exercises matching what is typed: every word, plural or not, somewhere in the name. */
function matchExercises(library, text) {
  const words = exerciseKey(text).split(' ').filter(Boolean).map(stem);
  if (!words.length) return library;
  return library.filter(e => words.every(w => e.key.includes(w)));
}

export const setsIn = (exercises = []) => exercises.reduce((n, ex) => n + (ex.sets?.length || 0), 0);
const describeSet = (s, unit = 'kg') => [s.reps != null ? `${s.reps} reps` : null, fmtWeight(s.weight, unit)].filter(Boolean).join(' · ') || 'set';

export const BTN = 'rounded-xl px-4 py-3 text-base font-semibold active:scale-[0.98] transition disabled:opacity-40';
export const INPUT = 'bg-[#141414] border border-[#2a2a2a] rounded-lg px-3 py-2 text-white text-base focus:outline-none focus:border-green-500 w-full';

// --- Home-screen web app ----------------------------------------------------------------

/** The gym's own manifest and iPhone tags while it is open, so "Add to Home Screen" opens straight into it. */
function useGymWebApp() {
  useEffect(() => {
    const added = [];
    const add = (tag, attrs) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      added.push(el);
    };
    add('link', { rel: 'manifest', href: '/gym.webmanifest' });
    add('link', { rel: 'apple-touch-icon', href: '/gym-app/apple-touch-icon.png' });
    add('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'apple-mobile-web-app-title', content: 'Gym' });
    add('meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' });
    const viewport = document.querySelector('meta[name="viewport"]');
    const before = viewport?.getAttribute('content');
    viewport?.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
    const title = document.title;
    document.title = 'Gym';
    return () => {
      added.forEach(el => el.remove());
      if (viewport && before) viewport.setAttribute('content', before);
      document.title = title;
    };
  }, []);
}

/** Keep the screen on during a workout, where the phone allows it. */
function useWakeLock(on) {
  useEffect(() => {
    if (!on || !navigator.wakeLock) return undefined;
    let lock = null;
    let cancelled = false;
    const take = () => navigator.wakeLock.request('screen').then(l => { if (cancelled) l.release(); else lock = l; }).catch(() => {});
    take();
    const again = () => { if (document.visibilityState === 'visible') take(); };
    document.addEventListener('visibilitychange', again);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', again); lock?.release().catch(() => {}); };
  }, [on]);
}

// A short beep for the end of a rest. Browsers only allow sound after a tap, so
// the context is made on the first tick.
let audio = null;
function unlockAudio() {
  try {
    audio = audio || new (window.AudioContext || window.webkitAudioContext)();
    if (audio.state === 'suspended') audio.resume();
  } catch { /* no sound, then */ }
}
function restOverAlert() {
  try { navigator.vibrate?.([200, 100, 200]); } catch { /* not on iPhone */ }
  if (!audio) return;
  [0, 0.25, 0.5].forEach((t, i) => {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.frequency.value = i === 2 ? 1320 : 880;
    gain.gain.setValueAtTime(0.25, audio.currentTime + t);
    gain.gain.exponentialRampToValueAtTime(0.001, audio.currentTime + t + 0.18);
    osc.connect(gain).connect(audio.destination);
    osc.start(audio.currentTime + t);
    osc.stop(audio.currentTime + t + 0.2);
  });
}

// --- The app ----------------------------------------------------------------------------

export default function Gym() {
  const token = useAuthStore(s => s.token);
  const userId = useAuthStore(s => s.user?.id);
  useGymWebApp();
  if (!token) {
    // Back here after signing in, by either route (password or Google).
    sessionStorage.setItem('mc_oauth_return', '/gym');
    return <Navigate to="/login" replace />;
  }
  return (
    <RabelProvider userId={userId}>
      <GymShell />
    </RabelProvider>
  );
}

function GymShell() {
  const rabel = useRabel();
  // The phone screens are one column; progress and the rehab charts use a laptop's width.
  const path = useLocation().pathname;
  const wide = path.startsWith('/gym/progress') || path.startsWith('/gym/rehab/charts');
  return (
    <div className={`min-h-[100dvh] bg-[#0a0a0a] text-white ${rabel.on ? 'gym-rabel' : ''}`} style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <style>{RABEL_CSS}</style>
      {rabel.on && <Flames />}
      <div className={`gym-content ${wide ? 'max-w-6xl px-6' : 'max-w-xl px-4'} mx-auto ${rabel.on ? 'pb-[max(10rem,calc(min(30vh,260px)+2rem))]' : 'pb-40'}`}>
        <Routes>
          <Route index element={<SessionList />} />
          <Route path="s/:id" element={<SessionEditor />} />
          <Route path="c/:name" element={<CategoryPage />} />
          <Route path="workout" element={<Workout />} />
          <Route path="history" element={<History />} />
          <Route path="history/:id" element={<Workout finished />} />
          <Route path="progress" element={<GymProgress />} />
          <Route path="rehab/*" element={<Rehab />} />
          <Route path="*" element={<Navigate to="/gym" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export function Header({ title, back, right }) {
  return (
    <div className="flex items-center gap-3 pt-4 pb-3">
      {back && <Link to={back} className="text-green-400 text-2xl leading-none px-1 -ml-1" aria-label="Back">‹</Link>}
      <h1 className="text-2xl font-bold truncate flex-1">{title}</h1>
      {right}
    </div>
  );
}

export function NotForYou() {
  return <div className="pt-24 text-center text-gray-500">Nothing here.</div>;
}

// --- Sessions ------------------------------------------------------------------------------

function SessionList() {
  const { say } = useRabel();
  return (
    <>
      <Header title={say('Gym', 'Gym, babe')} right={<><Link to="/gym/rehab" className="text-sm text-gray-400 px-2 py-1">Rehab</Link><Link to="/gym/progress" className="text-sm text-gray-400 px-2 py-1">Progress</Link><Link to="/gym/history" className="text-sm text-gray-400 px-2 py-1">History</Link></>} />
      <div className="mb-4 -mt-1"><RabelToggle /></div>
      <CheckIn />
      <GymHome />
    </>
  );
}

/** Make a run of sets from "3 × 10, 90 s rest". */
const makeSets = (count, reps, weight, rest) => Array.from({ length: count }, () => ({ id: newId(), reps, weight, rest }));

/**
 * The plan in blocks: an exercise on its own, or a circuit: neighbouring
 * exercises with the same circuit id, done round by round, one set each per
 * round. A circuit's rounds are its exercises' set counts.
 */
function blocksOf(exercises = []) {
  const blocks = [];
  exercises.forEach((ex, index) => {
    const prev = blocks[blocks.length - 1];
    if (ex.circuit && prev?.circuit === ex.circuit) prev.items.push({ ex, index });
    else blocks.push({ circuit: ex.circuit || null, items: [{ ex, index }] });
  });
  return blocks.map(b => ({ ...b, rounds: b.circuit ? Math.max(0, ...b.items.map(i => i.ex.sets.length)) : null }));
}

/** The order the sets are done in: an exercise's sets in turn; a circuit one round at a time. */
export function orderOf(exercises = []) {
  return blocksOf(exercises).flatMap((b) => {
    let out;
    if (!b.circuit) {
      const { ex } = b.items[0];
      out = ex.sets.map((st, n) => ({ ex, s: st, n, key: setKey(ex, st) }));
    } else {
      out = [];
      for (let r = 0; r < b.rounds; r++) {
        for (const { ex } of b.items) if (ex.sets[r]) out.push({ ex, s: ex.sets[r], n: r, key: setKey(ex, ex.sets[r]), round: true });
      }
    }
    // After the block's last set comes the rest before the next exercise, if one is set.
    const restAfter = b.items[b.items.length - 1].ex.restAfter;
    if (out.length) out[out.length - 1] = { ...out[out.length - 1], blockEnd: true, restAfter: restAfter ?? null };
    return out;
  });
}
const stepWord = (o) => (o?.round ? 'round' : 'set');
/** The rest after a set: the set's own, or at the end of an exercise the rest before the next. */
const restAfterSet = (o) => (o?.blockEnd && o.restAfter != null ? o.restAfter : (o?.s?.rest ?? 0));

function SessionEditor() {
  const { id } = useParams();
  const { say } = useRabel();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState('');
  const [adding, setAdding] = useState(null); // null, 'single' or 'circuit'
  const [library, setLibrary] = useState([]);
  const [categories, setCategories] = useState([]);
  const [calendar, setCalendar] = useState(false);
  const timer = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    gymAPI.session(id).then(r => setSession(r.data)).catch(() => setMissing(true));
    gymAPI.exercises().then(r => setLibrary(r.data)).catch(() => {});
    gymAPI.sessions().then(r => setCategories([...new Set(r.data.map(x => x.category).filter(Boolean))])).catch(() => {});
  }, [id]);
  const remember = (name) => setLibrary(lib => (lib.some(e => e.key === exerciseKey(name)) ? lib : [...lib, { key: exerciseKey(name), name: name.trim() }]));
  /** A name that is a known exercise in other words takes the known spelling. */
  const canonical = (name) => library.find(e => e.key === exerciseKey(name))?.name || name.trim();

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    const body = pending.current;
    pending.current = null;
    if (!body) return;
    setSaving('Saving…');
    try {
      await gymAPI.updateSession(id, body);
      setSaving('Saved');
    } catch {
      setSaving('Not saved: check your signal');
    }
  }, [id]);

  // Save a moment after the last change, and on the way out.
  const change = (patch) => {
    setSession(s => {
      const next = { ...s, ...patch };
      pending.current = { name: next.name, notes: next.notes, exercises: next.exercises, category: next.category || '' };
      return next;
    });
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  };
  useEffect(() => () => { flush(); }, [flush]);

  if (missing) return <><Header title="Not found" back="/gym" /></>;
  if (!session) return <p className="pt-6 text-gray-500">Loading…</p>;

  const exercises = session.exercises || [];
  const setExercises = (list) => change({ exercises: list });
  const updateEx = (i, patch) => setExercises(exercises.map((e, n) => (n === i ? { ...e, ...patch } : e)));
  const blocks = blocksOf(exercises);
  const flatten = (list) => list.flatMap(b => b.items.map(i => i.ex));
  // A circuit moves as one.
  const moveBlock = (bi, d) => {
    const j = bi + d;
    if (j < 0 || j >= blocks.length) return;
    const list = blocks.slice();
    [list[bi], list[j]] = [list[j], list[bi]];
    setExercises(flatten(list));
  };
  const replaceBlock = (bi, exs) => setExercises(flatten(blocks.map((b, n) => (n === bi ? { items: exs.map(ex => ({ ex })) } : b))));

  async function start() {
    await flush();
    await gymAPI.startRun(id);
    navigate('/gym/workout');
  }
  async function duplicate() {
    await flush();
    const r = await gymAPI.duplicateSession(id);
    navigate(`/gym/s/${r.data.id}`);
  }
  async function remove() {
    if (!window.confirm(`Delete "${session.name}"? Workouts already done from it stay in the history.`)) return;
    clearTimeout(timer.current);
    pending.current = null;
    await gymAPI.deleteSession(id);
    navigate('/gym');
  }

  return (
    <>
      <Header back={session.category ? `/gym/c/${encodeURIComponent(session.category)}` : '/gym'} title={
        <input value={session.name} onChange={e => change({ name: e.target.value })} className="bg-transparent w-full focus:outline-none" aria-label="Session name" />
      } />
      <div className="text-xs text-gray-600 -mt-2 mb-2 h-4">{saving}</div>
      <CategoryPicker value={session.category || ''} known={categories} onChange={category => change({ category })} />

      <button onClick={start} disabled={!setsIn(exercises)} className={`${BTN} w-full bg-green-500 text-black text-lg mb-4`}>{say('Start workout', 'Let\'s slay 💅🔥')}</button>

      <textarea
        value={session.notes || ''}
        onChange={e => change({ notes: e.target.value })}
        placeholder="Notes (warm-up, how it went last time…)"
        rows={2}
        className={`${INPUT} mb-4 text-sm`}
      />

      <div className="space-y-3">
        {blocks.map((b, bi) => {
          const up = bi > 0 ? () => moveBlock(bi, -1) : null;
          const down = bi < blocks.length - 1 ? () => moveBlock(bi, 1) : null;
          if (b.circuit) {
            return (
              <CircuitCard
                key={b.circuit}
                block={b}
                library={library}
                lastRun={session.last_run}
                inSession={exercises.map(e => exerciseKey(e.name))}
                canonical={canonical}
                remember={remember}
                onChange={exs => replaceBlock(bi, exs)}
                onRemove={() => { if (window.confirm('Remove this circuit and its exercises?')) replaceBlock(bi, []); }}
                onUp={up}
                onDown={down}
              />
            );
          }
          const { ex, index: i } = b.items[0];
          return (
            <ExerciseCard
              key={ex.id}
              ex={ex}
              last={lastFor(session.last_run, ex)}
              onChange={patch => updateEx(i, patch)}
              library={library}
              onNameDone={name => { const known = canonical(name); if (known !== name) updateEx(i, { name: known }); remember(known); }}
              onRemove={() => { if (window.confirm(`Remove ${ex.name}?`)) setExercises(exercises.filter((_, n) => n !== i)); }}
              onUp={up}
              onDown={down}
            />
          );
        })}
      </div>

      {adding === 'from'
        ? <AddFromSession currentId={id} onCancel={() => setAdding(null)} onAdd={list => { setExercises([...exercises, ...list]); list.forEach(x => remember(x.name)); setAdding(null); }} />
        : adding
        ? (
          <AddExercise
            mode={adding}
            library={library}
            inSession={exercises.map(e => exerciseKey(e.name))}
            onCancel={() => setAdding(null)}
            onAdd={ex => {
              const named = { ...ex, name: canonical(ex.name), ...(adding === 'circuit' ? { circuit: newId() } : {}) };
              setExercises([...exercises, named]);
              remember(named.name);
              setAdding(null);
            }}
          />
        )
        : (
          <>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={() => setAdding('single')} className={`${BTN} bg-[#141414] border border-dashed border-[#333] text-green-400`}>+ Add exercise</button>
              <button onClick={() => setAdding('circuit')} className={`${BTN} bg-[#141414] border border-dashed border-[#333] text-green-400`}>+ Add circuit</button>
            </div>
            <button onClick={() => setAdding('from')} className={`${BTN} w-full mt-2 bg-[#141414] border border-dashed border-[#333] text-green-400 text-sm`}>+ Add everything from a session</button>
          </>
        )}

      {calendar
        ? <AddToCalendar session={session} onClose={() => setCalendar(false)} />
        : <button onClick={() => setCalendar(true)} className={`${BTN} w-full mt-6 bg-[#141414] border border-[#2a2a2a] text-gray-200 text-sm`}>📅 Add to calendar</button>}

      <div className="flex gap-2 mt-2">
        <button onClick={duplicate} className={`${BTN} flex-1 bg-[#141414] border border-[#2a2a2a] text-gray-200 text-sm`}>Duplicate</button>
        <button onClick={remove} className={`${BTN} flex-1 bg-[#141414] border border-[#2a2a2a] text-red-400 text-sm`}>Delete</button>
      </div>
    </>
  );
}

/** What was done for an exercise last time, by set: the numbers to beat. */
function lastFor(lastRun, ex) {
  if (!lastRun?.state) return null;
  const done = lastRun.state.done || {};
  const prev = (lastRun.state.exercises || []).find(e => e.id === ex.id || e.name.toLowerCase() === ex.name.toLowerCase());
  if (!prev) return null;
  const sets = prev.sets.map(s => done[setKey(prev, s)]).filter(Boolean);
  if (!sets.length) return null;
  const weighted = sets.some(d => d.weight != null);
  const unit = unitOf(ex);
  return { at: lastRun.finished_at, text: sets.map(d => `${d.reps ?? '-'}${d.weight != null ? `×${plain(fromKg(d.weight, unit))}` : ''}`).join(', ') + (weighted ? ` ${unit}` : '') };
}

/**
 * Add an exercise. mode: 'single' (sets of it in turn), 'circuit' (start a
 * circuit: the count is its rounds) or 'circuit-add' (one more in a circuit
 * of `rounds`: no count to give).
 */
function AddExercise({ library = [], inSession = [], onAdd, onCancel, mode = 'single', rounds = 3 }) {
  const inCircuit = mode !== 'single';
  const [name, setName] = useState('');
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState(10);
  const [weight, setWeight] = useState(null); // kg
  const [unit, setUnit] = useState('kg');
  const [rest, setRest] = useState(inCircuit ? 30 : 90);
  const [open, setOpen] = useState(true);
  const ok = name.trim() && sets > 0;
  const exact = library.find(e => e.key === exerciseKey(name));
  const matches = matchExercises(library, name).filter(e => e.key !== exactKey(exact)).slice(0, 8);
  function pick(e) {
    setName(e.name);
    setOpen(false);
    if (e.plan) {
      // A circuit keeps its own rounds and short rests; the weight and reps still carry over.
      if (!inCircuit) { setSets(e.plan.sets || 3); setRest(e.plan.rest ?? 90); }
      setReps(e.plan.reps ?? 10);
      setWeight(e.plan.weight ?? null);
      setUnit(e.plan.unit === 'lb' ? 'lb' : 'kg');
    }
  }
  return (
    <div className="mt-3 rounded-2xl bg-[#141414] border border-green-800 p-4 space-y-3">
      <div className="relative">
        <input
          autoFocus
          value={name}
          onChange={e => { setName(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={library.length ? 'Pick one you have done, or type a new one' : 'Exercise, e.g. Lateral raise'}
          className={INPUT}
        />
        {name.trim() && (
          <div className="mt-1 text-xs">
            {exact
              ? <span className="text-green-400">✓ Tracked with your other "{exact.name}" workouts{inSession.includes(exact.key) ? ' (already in this session)' : ''}</span>
              : <span className="text-gray-500">New exercise: it starts its own history{matches.length ? '. Is it one of these?' : '.'}</span>}
          </div>
        )}
        {open && matches.length > 0 && (
          <div className="mt-2 rounded-xl border border-[#2a2a2a] divide-y divide-[#222] max-h-64 overflow-y-auto">
            {matches.map(e => (
              <button key={e.key} onClick={() => pick(e)} className="w-full text-left px-3 py-2.5 flex items-center gap-2 active:bg-[#1a1a1a]">
                <span className="flex-1 text-white">{e.name}</span>
                {e.plan && <span className="text-xs text-gray-500">{e.plan.sets} × {e.plan.reps ?? '-'}{e.plan.weight != null ? ` @ ${fmtWeight(e.plan.weight, e.plan.unit)}` : ''}</span>}
                {inSession.includes(e.key) && <span className="text-[10px] text-gray-600">in session</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      {mode === 'circuit' && <div className="text-xs text-gray-500">A circuit: its exercises one after another, then round again. Add the rest to it next.</div>}
      <div className={`grid ${mode === 'circuit-add' ? 'grid-cols-3' : 'grid-cols-4'} gap-2`}>
        {mode !== 'circuit-add' && <Field label={mode === 'circuit' ? 'Rounds' : 'Sets'}><input inputMode="numeric" value={sets} onChange={e => setSets(Number(e.target.value.replace(/\D/g, '')) || 0)} className={INPUT} /></Field>}
        <Field label="Reps"><input inputMode="numeric" value={reps} onChange={e => setReps(Number(e.target.value.replace(/\D/g, '')) || 0)} className={INPUT} /></Field>
        <Field label="Weight"><WeightInput kg={weight} unit={unit} onChange={setWeight} onToggleUnit={() => setUnit(otherUnit(unit))} className={INPUT} /></Field>
        <Field label={inCircuit ? 'Rest after s' : 'Rest s'}><input inputMode="numeric" value={rest} onChange={e => setRest(Number(e.target.value.replace(/\D/g, '')) || 0)} className={INPUT} /></Field>
      </div>
      <div className="flex gap-2">
        <button disabled={!ok} onClick={() => onAdd({ id: newId(), name: name.trim(), sets: makeSets(Math.min(mode === 'circuit-add' ? rounds : sets, 30), reps || null, weight, rest), ...(unit === 'lb' ? { unit: 'lb' } : {}) })} className={`${BTN} flex-1 bg-green-500 text-black`}>{mode === 'circuit' ? 'Start circuit' : 'Add'}</button>
        <button onClick={onCancel} className={`${BTN} bg-transparent text-gray-400`}>Cancel</button>
      </div>
    </div>
  );
}

const exactKey = (e) => e?.key ?? null;

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ExerciseCard({ ex, last, onChange, onRemove, onUp, onDown, library = [], onNameDone }) {
  const setSet = (n, patch) => onChange({ sets: ex.sets.map((s, i) => (i === n ? { ...s, ...patch } : s)) });
  const num = (v, decimal) => {
    const clean = String(v).replace(decimal ? /[^\d.]/g : /\D/g, '');
    return clean === '' ? null : Number(clean);
  };
  return (
    <div className="rounded-2xl bg-[#141414] border border-[#232323] p-3">
      <div className="flex items-center gap-2 mb-2">
        <input
          value={ex.name}
          list={`gym-exercises-${ex.id}`}
          onChange={e => onChange({ name: e.target.value })}
          onBlur={e => onNameDone?.(e.target.value)}
          className="bg-transparent text-lg font-semibold flex-1 min-w-0 focus:outline-none"
        />
        <datalist id={`gym-exercises-${ex.id}`}>
          {library.map(e => <option key={e.key} value={e.name} />)}
        </datalist>
        <button onClick={onUp} disabled={!onUp} className="text-gray-500 px-2 disabled:opacity-20" aria-label="Move up">▲</button>
        <button onClick={onDown} disabled={!onDown} className="text-gray-500 px-2 disabled:opacity-20" aria-label="Move down">▼</button>
        <button onClick={onRemove} className="text-gray-600 px-2" aria-label="Remove">✕</button>
      </div>
      {last && <div className="text-xs text-gray-500 mb-2">Last time: {last.text}</div>}
      <div className="grid grid-cols-[2rem_1fr_1fr_1fr_2rem] gap-2 text-[11px] uppercase tracking-wide text-gray-500 mb-1 px-0.5">
        <span>Set</span><span>Reps</span><span>Weight</span><span>Rest s</span><span />
      </div>
      <div className="space-y-1.5">
        {ex.sets.map((s, n) => (
          <div key={s.id} className="grid grid-cols-[2rem_1fr_1fr_1fr_2rem] gap-2 items-center">
            <span className="text-gray-500 text-center">{n + 1}</span>
            <input inputMode="numeric" value={s.reps ?? ''} onChange={e => setSet(n, { reps: num(e.target.value) })} className={`${INPUT} py-1.5`} />
            <WeightInput kg={s.weight} unit={unitOf(ex)} onChange={kg => setSet(n, { weight: kg })} onToggleUnit={() => onChange({ unit: otherUnit(unitOf(ex)) })} className={`${INPUT} py-1.5`} />
            <input inputMode="numeric" value={s.rest ?? ''} onChange={e => setSet(n, { rest: num(e.target.value) ?? 0 })} className={`${INPUT} py-1.5`} />
            <button onClick={() => onChange({ sets: ex.sets.filter((_, i) => i !== n) })} className="text-gray-600" aria-label="Remove set">−</button>
          </div>
        ))}
      </div>
      <button
        onClick={() => { const lastSet = ex.sets[ex.sets.length - 1] || { reps: 10, weight: null, rest: 90 }; onChange({ sets: [...ex.sets, { ...lastSet, id: newId() }] }); }}
        className="mt-2 text-sm text-green-400 py-1"
      >
        + Set
      </button>
      <RestAfter value={ex.restAfter} fallback={ex.sets[ex.sets.length - 1]?.rest} label="Rest before the next exercise" onChange={v => onChange({ restAfter: v })} />
    </div>
  );
}

/** The rest before the next exercise: its own number, or blank for the last set's rest. */
function RestAfter({ value, fallback, label, onChange }) {
  return (
    <label className="flex items-center gap-2 mt-2 pt-2 border-t border-[#232323] text-sm text-gray-400">
      <span className="flex-1">{label}</span>
      <input
        inputMode="numeric"
        value={value ?? ''}
        placeholder={fallback != null ? String(fallback) : '-'}
        onChange={e => { const clean = e.target.value.replace(/\D/g, ''); onChange(clean === '' ? null : Number(clean)); }}
        className={`${INPUT} py-1.5 !w-20 shrink-0 text-right`}
      />
      <span className="text-gray-500">s</span>
    </label>
  );
}

/**
 * A circuit in the editor: one row per exercise (reps, kg, rest after it, the
 * same every round) and the number of rounds. The last exercise's rest is the
 * rest between rounds.
 */
function CircuitCard({ block, library, lastRun, inSession, canonical, remember, onChange, onRemove, onUp, onDown }) {
  const [adding, setAdding] = useState(false);
  const items = block.items.map(i => i.ex);
  const rounds = block.rounds || 1;
  const num = (v, decimal) => {
    const clean = String(v).replace(decimal ? /[^\d.]/g : /\D/g, '');
    return clean === '' ? null : Number(clean);
  };
  const setRounds = (n) => {
    const count = Math.max(1, Math.min(30, n));
    onChange(items.map(ex => ({ ...ex, sets: Array.from({ length: count }, (_, r) => ex.sets[r] || { ...(ex.sets[ex.sets.length - 1] || { reps: 10, weight: null, rest: 30 }), id: newId() }) })));
  };
  const setRow = (i, patch) => onChange(items.map((ex, n) => (n !== i ? ex : { ...ex, sets: ex.sets.map(st => ({ ...st, ...patch })) })));
  const rename = (i, name) => onChange(items.map((ex, n) => (n !== i ? ex : { ...ex, name })));
  const moveRow = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const list = items.slice();
    [list[i], list[j]] = [list[j], list[i]];
    onChange(list);
  };
  const removeRow = (i) => {
    if (!window.confirm(`Take ${items[i].name} out of the circuit?`)) return;
    onChange(items.filter((_, n) => n !== i));
  };

  return (
    <div className="rounded-2xl bg-[#141414] border border-green-800 p-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs uppercase tracking-wide text-green-400 font-semibold flex-1">Circuit</span>
        <div className="flex items-center gap-1 rounded-full border border-[#2a2a2a] px-1">
          <button onClick={() => setRounds(rounds - 1)} className="w-8 h-8 text-lg text-gray-300" aria-label="One round fewer">−</button>
          <span className="text-sm tabular-nums">{rounds} round{rounds === 1 ? '' : 's'}</span>
          <button onClick={() => setRounds(rounds + 1)} className="w-8 h-8 text-lg text-gray-300" aria-label="One more round">+</button>
        </div>
        <button onClick={onUp} disabled={!onUp} className="text-gray-500 px-2 disabled:opacity-20" aria-label="Move up">▲</button>
        <button onClick={onDown} disabled={!onDown} className="text-gray-500 px-2 disabled:opacity-20" aria-label="Move down">▼</button>
        <button onClick={onRemove} className="text-gray-600 px-2" aria-label="Remove circuit">✕</button>
      </div>
      <div className="text-xs text-gray-500 mb-2">Each exercise once, then round again. The last rest is the rest between rounds.</div>
      <div className="grid grid-cols-[1fr_3.5rem_3.5rem_3.5rem_3.25rem] gap-1.5 text-[11px] uppercase tracking-wide text-gray-500 mb-1 px-0.5">
        <span>Exercise</span><span>Reps</span><span>Weight</span><span>Rest s</span><span />
      </div>
      <div className="space-y-1.5">
        {items.map((ex, i) => {
          const first = ex.sets[0] || {};
          const last = lastFor(lastRun, ex);
          const isLastRow = i === items.length - 1;
          return (
            <div key={ex.id}>
              <div className="grid grid-cols-[1fr_3.5rem_3.5rem_3.5rem_3.25rem] gap-1.5 items-center">
                <input
                  value={ex.name}
                  list={`gym-exercises-${ex.id}`}
                  onChange={e => rename(i, e.target.value)}
                  onBlur={e => { const known = canonical(e.target.value); if (known !== e.target.value) rename(i, known); remember(known); }}
                  className={`${INPUT} py-1.5 min-w-0`}
                />
                <datalist id={`gym-exercises-${ex.id}`}>{library.map(e => <option key={e.key} value={e.name} />)}</datalist>
                <input inputMode="numeric" value={first.reps ?? ''} onChange={e => setRow(i, { reps: num(e.target.value) })} className={`${INPUT} py-1.5 px-2`} />
                <WeightInput kg={first.weight} unit={unitOf(ex)} onChange={kg => setRow(i, { weight: kg })} onToggleUnit={() => onChange(items.map((x, n) => (n !== i ? x : { ...x, unit: otherUnit(unitOf(x)) })))} className={`${INPUT} py-1.5 px-2`} />
                <input inputMode="numeric" value={first.rest ?? ''} onChange={e => setRow(i, { rest: num(e.target.value) ?? 0 })} className={`${INPUT} py-1.5 px-2`} title={isLastRow ? 'Rest between rounds' : 'Rest before the next exercise'} />
                <div className="flex">
                  <button onClick={() => moveRow(i, -1)} disabled={i === 0} className="text-gray-500 w-5 disabled:opacity-20 text-xs" aria-label="Earlier in the circuit">▲</button>
                  <button onClick={() => removeRow(i)} className="text-gray-600 w-6" aria-label="Take out of the circuit">−</button>
                </div>
              </div>
              {(last || isLastRow) && (
                <div className="text-[11px] text-gray-600 mt-0.5 px-1">
                  {last && <>Last time: {last.text}</>}{last && isLastRow ? ' · ' : ''}{isLastRow && 'its rest is the rest between rounds'}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {items.length > 0 && (
        <RestAfter
          value={items[items.length - 1].restAfter}
          fallback={items[items.length - 1].sets[0]?.rest}
          label="Rest after the circuit"
          onChange={v => onChange(items.map((x, n) => (n === items.length - 1 ? { ...x, restAfter: v } : x)))}
        />
      )}
      {adding
        ? (
          <AddExercise
            mode="circuit-add"
            rounds={rounds}
            library={library}
            inSession={inSession}
            onCancel={() => setAdding(false)}
            onAdd={ex => { onChange([...items, { ...ex, name: canonical(ex.name), circuit: block.circuit }]); remember(ex.name); setAdding(false); }}
          />
        )
        : <button onClick={() => setAdding(true)} className="mt-2 text-sm text-green-400 py-1">+ Add to circuit</button>}
    </div>
  );
}

// --- Following a session ----------------------------------------------------------------------

/**
 * A workout: the one in progress, or with `finished`, one from History opened
 * to correct what was logged. Both can be changed as you go: move an exercise
 * (the machine's busy, do it last), remove one, add one, remove a set. A
 * finished workout has no rest timer and saves as you edit.
 */
function Workout({ finished = false }) {
  const navigate = useNavigate();
  const { id: runId } = useParams();
  const rabel = useRabel();
  const [burst, setBurst] = useState(null);
  const [run, setRun] = useState(undefined);
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [editing, setEditing] = useState(null); // set key being adjusted
  const [finishing, setFinishing] = useState(false);
  const [menu, setMenu] = useState(null); // the block whose changes are open
  const [adding, setAdding] = useState(false);
  const [library, setLibrary] = useState([]);
  const saveTimer = useRef(null);
  const pending = useRef(null);
  const alerted = useRef(null);

  useEffect(() => {
    (finished ? gymAPI.run(runId) : gymAPI.activeRun()).then(r => {
      setRun(r.data);
      if (r.data?.session_id && !finished) gymAPI.session(r.data.session_id).then(s => setSession(s.data)).catch(() => {});
    }).catch(() => setRun(null));
    gymAPI.exercises().then(r => setLibrary(r.data)).catch(() => {});
  }, [finished, runId]);

  useWakeLock(!!run && !finished);

  // The clock for the rest timer: a quarter-second tick, off real time, so a locked phone loses nothing.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  const flush = useCallback(() => {
    clearTimeout(saveTimer.current);
    const body = pending.current;
    pending.current = null;
    if (body) return gymAPI.updateRun(body.id, { state: body.state }).catch(() => { pending.current = pending.current || body; });
    return Promise.resolve();
  }, []);
  useEffect(() => {
    const out = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', out);
    return () => { document.removeEventListener('visibilitychange', out); flush(); };
  }, [flush]);

  const state = run?.state || { exercises: [], done: {}, rest: null };
  const exercises = state.exercises || [];
  const done = state.done || {};
  const rest = state.rest;
  const remaining = rest ? rest.endsAt - now : 0;

  // Beep and buzz once as a rest reaches zero.
  useEffect(() => {
    if (rest && remaining <= 0 && alerted.current !== rest.endsAt) {
      alerted.current = rest.endsAt;
      if (now - rest.endsAt < 5000) restOverAlert();
    }
  }, [rest, remaining, now]);

  const order = useMemo(() => orderOf(exercises), [exercises]);
  const next = order.find(o => !done[o.key]);
  const inOrder = useMemo(() => new Map(order.map(o => [o.key, o])), [order]);

  if (run === undefined) return <p className="pt-6 text-gray-500">Loading…</p>;
  if (!run) return <Navigate to={finished ? '/gym/history' : '/gym'} replace />;

  function update(patch) {
    setRun(r => {
      const nextRun = { ...r, state: { ...r.state, ...patch } };
      pending.current = { id: r.id, state: nextRun.state };
      return nextRun;
    });
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, 500);
  }

  function tick(o) {
    unlockAudio();
    if (done[o.key]) {
      const rest2 = { ...done };
      delete rest2[o.key];
      update({ done: rest2 });
      return;
    }
    const isLast = order.filter(x => !done[x.key] && x.key !== o.key).length === 0;
    if (finished) {
      update({ done: { ...done, [o.key]: { at: run.finished_at, reps: o.s.reps ?? null, weight: o.s.weight ?? null } } });
      return;
    }
    if (rabel.on) {
      const b = { id: Date.now(), text: isLast ? 'QUEEN 👑 Done!' : sassLine() };
      setBurst(b);
      setTimeout(() => setBurst(cur => (cur?.id === b.id ? null : cur)), 1400);
    }
    update({
      done: { ...done, [o.key]: { at: new Date().toISOString(), reps: o.s.reps ?? null, weight: o.s.weight ?? null } },
      // The last set of the workout has nothing to rest for.
      rest: (() => {
        const seconds = restAfterSet(inOrder.get(o.key) || o);
        return seconds > 0 && !isLast ? { endsAt: Date.now() + seconds * 1000, seconds, label: `${o.ex.name}, ${stepWord(o)} ${o.n + 1}` } : null;
      })(),
    });
  }

  function adjustSet(exId, setId, patch) {
    const key = `${exId}/${setId}`;
    const logged = done[key] ? { done: { ...done, [key]: { ...done[key], ...('reps' in patch ? { reps: patch.reps } : {}), ...('weight' in patch ? { weight: patch.weight } : {}) } } } : {};
    update({ exercises: exercises.map(ex => (ex.id !== exId ? ex : { ...ex, sets: ex.sets.map(s => (s.id === setId ? { ...s, ...patch } : s)) })), ...logged });
  }
  /** One more round of a circuit: a set more of each of its exercises. */
  function addRound(circuitId) {
    update({ exercises: exercises.map(ex => (ex.circuit !== circuitId ? ex : { ...ex, sets: [...ex.sets, { ...(ex.sets[ex.sets.length - 1] || { reps: 10, weight: null, rest: 30 }), id: newId() }] })) });
  }
  function addSet(exId) {
    update({ exercises: exercises.map(ex => (ex.id !== exId ? ex : { ...ex, sets: [...ex.sets, { ...(ex.sets[ex.sets.length - 1] || { reps: 10, weight: null, rest: 90 }), id: newId() }] })) });
  }
  const blocks = blocksOf(exercises);
  const fromBlocks = (list) => list.flatMap(b => b.items.map(i => i.ex));
  function moveBlock(i, d) {
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const list = blocks.slice();
    [list[i], list[j]] = [list[j], list[i]];
    update({ exercises: fromBlocks(list) });
  }
  /** The machine's busy: this one goes to the end, everything else moves up. */
  function doLast(i) {
    const list = blocks.slice();
    const [b] = list.splice(i, 1);
    list.push(b);
    update({ exercises: fromBlocks(list) });
    setMenu(null);
  }
  const loggedIn = (exs) => exs.reduce((n, ex) => n + ex.sets.filter(st => done[setKey(ex, st)]).length, 0);
  function removeExercises(ids, what) {
    const logged = loggedIn(exercises.filter(ex => ids.includes(ex.id)));
    if (!window.confirm(logged ? `Remove ${what}? The ${logged} set${logged === 1 ? '' : 's'} logged for it go too.` : `Remove ${what} from this workout?`)) return;
    const keep = Object.fromEntries(Object.entries(done).filter(([k]) => !ids.some(id => k.startsWith(`${id}/`))));
    update({ exercises: exercises.filter(ex => !ids.includes(ex.id)), done: keep });
    setMenu(null);
  }
  function removeSet(exId, setId) {
    const keep = { ...done };
    delete keep[`${exId}/${setId}`];
    // An exercise left with no sets goes with its last one.
    update({ exercises: exercises.map(ex => (ex.id !== exId ? ex : { ...ex, sets: ex.sets.filter(st => st.id !== setId) })).filter(ex => ex.sets.length), done: keep });
    setEditing(null);
  }
  const nudgeRest = (seconds) => rest && update({ rest: { ...rest, endsAt: Math.max(Date.now(), rest.endsAt) + seconds * 1000 } });

  const changed = session && JSON.stringify(stripDone(session.exercises)) !== JSON.stringify(stripDone(exercises));

  async function finish(saveToSession) {
    await flush();
    await gymAPI.updateRun(run.id, { state: { ...state, rest: null }, finished: true });
    if (saveToSession && run.session_id) await gymAPI.updateSession(run.session_id, { exercises });
    navigate('/gym');
  }
  async function discard() {
    if (!window.confirm(finished ? 'Delete this workout from your history? Its sets come out of your progress too.' : 'Throw this workout away? Nothing from it is kept.')) return;
    clearTimeout(saveTimer.current);
    pending.current = null;
    await gymAPI.deleteRun(run.id);
    navigate(finished ? '/gym/history' : '/gym');
  }

  const doneCount = Object.keys(done).length;
  const elapsed = now - new Date(run.started_at).getTime();

  /** One set to tick: numbered in an exercise, named in a circuit round. */
  function setRow(o, number, title = null) {
    const { ex, s: st, key } = o;
    const isDone = !!done[key];
    const isNext = next?.key === key;
    return (
      <div key={st.id}>
        <div className={`flex items-center gap-3 rounded-xl px-2 py-2 ${isNext ? 'bg-green-500/10 border border-green-800' : 'border border-transparent'}`}>
          <button
            onClick={() => tick(o)}
            className={`w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-2xl border-2 ${isDone ? 'bg-green-500 border-green-500 text-black' : isNext ? 'border-green-500 text-transparent' : 'border-[#3a3a3a] text-transparent'}`}
            aria-label={isDone ? 'Untick' : 'Done'}
          >
            ✓
          </button>
          <button onClick={() => setEditing(editing === key ? null : key)} className="flex-1 text-left min-w-0">
            <div className={`text-base ${isDone ? 'text-gray-500' : 'text-white'}`}>
              {title ? <span className="font-semibold mr-2">{title}</span> : <span className="text-gray-500 mr-2">{number}</span>}
              {describeSet(st, unitOf(ex))}
            </div>
            <div className="text-xs text-gray-600">
              {inOrder.get(key)?.blockEnd && inOrder.get(key)?.restAfter != null ? `rest ${fmtRest(inOrder.get(key).restAfter)} before the next exercise` : `rest ${fmtRest(st.rest)}`}
            </div>
          </button>
        </div>
        {editing === key && (
          <div className="grid grid-cols-3 gap-2 px-2 pt-2 pb-1">
            <Stepper label="Reps" value={st.reps} step={1} onChange={v => adjustSet(ex.id, st.id, { reps: v })} />
            <Stepper
              label={<UnitSwitch unit={unitOf(ex)} onToggle={() => update({ exercises: exercises.map(x => (x.id !== ex.id ? x : { ...x, unit: otherUnit(unitOf(x)) })) })} className="uppercase !text-[10px]" />}
              value={fromKg(st.weight, unitOf(ex))}
              step={weightStep(unitOf(ex))}
              onChange={v => adjustSet(ex.id, st.id, { weight: toKg(v, unitOf(ex)) })}
            />
            <Stepper label="Rest s" value={st.rest} step={15} onChange={v => adjustSet(ex.id, st.id, { rest: v ?? 0 })} />
            <button onClick={() => removeSet(ex.id, st.id)} className="col-span-3 text-xs text-red-400 py-1">Remove this {o.round ? 'round' : 'set'}{o.round ? ` of ${ex.name}` : ''}</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Header
        title={run.name}
        back={finished ? '/gym/history' : '/gym'}
        right={finished
          ? <span className="text-sm text-gray-400">{new Date(run.finished_at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
          : <><span className="text-sm text-gray-400 tabular-nums">{fmtClock(elapsed)}</span><RabelToggle compact /></>}
      />
      {finished && <p className="text-xs text-gray-500 -mt-2 mb-3">Editing a finished workout. Changes save as you go and update your progress.</p>}
      <div className="h-1.5 rounded-full bg-[#1a1a1a] mb-4 overflow-hidden">
        <div className="h-full bg-green-500 transition-all" style={{ width: `${order.length ? (doneCount / order.length) * 100 : 0}%` }} />
      </div>

      <div className="space-y-4">
        {blocks.map((b, bi) => {
          const blockKey = b.circuit || b.items[0].ex.id;
          const changes = menu === blockKey && (
            <BlockChanges
              first={bi === 0}
              last={bi === blocks.length - 1}
              finished={finished}
              onUp={() => moveBlock(bi, -1)}
              onDown={() => moveBlock(bi, 1)}
              onLast={() => doLast(bi)}
              onRemove={() => removeExercises(b.items.map(i => i.ex.id), b.circuit ? 'this circuit' : b.items[0].ex.name)}
              parts={b.circuit && b.items.length > 1 ? b.items.map(i => ({ name: i.ex.name, remove: () => removeExercises([i.ex.id], `${i.ex.name} from the circuit`) })) : null}
            />
          );
          const toggle = <button onClick={() => setMenu(menu === blockKey ? null : blockKey)} className="text-gray-400 px-2 -mr-1 text-xl leading-none" aria-label="Change this exercise">⋯</button>;
          if (!b.circuit) {
            const { ex } = b.items[0];
            const last = lastFor(session?.last_run, ex);
            return (
              <div key={ex.id} className="rounded-2xl bg-[#141414] border border-[#232323] p-3">
                <div className="flex items-start gap-2"><div className="text-lg font-semibold flex-1">{ex.name}</div>{toggle}</div>
                {changes}
                {last && <div className="text-xs text-gray-500">Last time: {last.text}</div>}
                <div className="mt-2 space-y-2">
                  {ex.sets.map((st, n) => setRow({ ex, s: st, n, key: setKey(ex, st) }, n + 1))}
                </div>
                <button onClick={() => addSet(ex.id)} className="mt-1 text-sm text-green-400 py-1 px-2">+ Set</button>
              </div>
            );
          }
          const exs = b.items.map(i => i.ex);
          const roundsDone = Array.from({ length: b.rounds }, (_, r) => exs.every(ex => !ex.sets[r] || done[setKey(ex, ex.sets[r])])).filter(Boolean).length;
          return (
            <div key={b.circuit} className="rounded-2xl bg-[#141414] border border-green-800 p-3">
              <div className="flex items-baseline gap-2">
                <span className="text-xs uppercase tracking-wide text-green-400 font-semibold">Circuit</span>
                <span className="text-sm text-gray-400 flex-1 truncate">{exs.map(ex => ex.name).join(' · ')}</span>
                <span className="text-xs text-gray-500 tabular-nums">{roundsDone}/{b.rounds} rounds</span>
                {toggle}
              </div>
              {changes}
              {Array.from({ length: b.rounds }, (_, r) => (
                <div key={r} className="mt-3">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 px-2 mb-1">Round {r + 1}</div>
                  <div className="space-y-2">
                    {exs.map(ex => ex.sets[r] && setRow({ ex, s: ex.sets[r], n: r, key: setKey(ex, ex.sets[r]), round: true }, null, ex.name))}
                  </div>
                </div>
              ))}
              <button onClick={() => addRound(b.circuit)} className="mt-2 text-sm text-green-400 py-1 px-2">+ Round</button>
            </div>
          );
        })}
      </div>

      {adding ? (
        <AddExercise
          library={library}
          inSession={exercises.map(ex => exerciseKey(ex.name))}
          onAdd={ex => { update({ exercises: [...exercises, ex] }); setAdding(false); }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <button onClick={() => setAdding(true)} className={`${BTN} w-full mt-3 bg-[#141414] border border-dashed border-[#333] text-green-400`}>+ Add exercise</button>
      )}

      <div className="mt-8 space-y-2">
        {finished ? null : finishing ? (
          <div className="rounded-2xl bg-[#141414] border border-[#2a2a2a] p-4 space-y-2">
            <div className="font-semibold">{doneCount} of {order.length} sets done</div>
            {changed && <p className="text-sm text-gray-400">You changed this workout. Keep "{run.name}" as it was for next time, or save today's changes to it?</p>}
            <button onClick={() => finish(false)} className={`${BTN} w-full bg-green-500 text-black`}>{changed ? 'Finish, keep the session as it was' : 'Finish'}</button>
            {changed && <button onClick={() => finish(true)} className={`${BTN} w-full bg-[#1f1f1f] text-white`}>Finish and save the changes to the session</button>}
            <button onClick={() => setFinishing(false)} className={`${BTN} w-full bg-transparent text-gray-400`}>Not yet</button>
          </div>
        ) : (
          <button onClick={() => setFinishing(true)} className={`${BTN} w-full bg-[#141414] border border-[#2a2a2a] text-white`}>{rabel.say('Finish workout', 'Finish, I\'m done serving 💁‍♀️')}</button>
        )}
        <button onClick={discard} className="w-full text-center text-sm text-gray-600 py-2">{finished ? 'Delete this workout' : 'Discard workout'}</button>
      </div>

      {!finished && <RestBar rest={rest} remaining={remaining} next={next} onNudge={nudgeRest} onSkip={() => update({ rest: null })} />}
      <Burst burst={burst} />
    </>
  );
}

/** What can be done to an exercise (or circuit) in a workout. */
function BlockChanges({ first, last, finished, onUp, onDown, onLast, onRemove, parts }) {
  const chip = 'px-3 py-2 rounded-lg bg-[#1f1f1f] text-sm disabled:opacity-30';
  return (
    <div className="flex flex-wrap gap-2 mt-2 mb-1">
      {!finished && !last && <button onClick={onLast} className={`${chip} text-amber-300`}>Machine busy? Do it last</button>}
      <button onClick={onUp} disabled={first} className={chip}>↑ Earlier</button>
      <button onClick={onDown} disabled={last} className={chip}>↓ Later</button>
      {parts?.map(p => <button key={p.name} onClick={p.remove} className={`${chip} text-red-400`}>Remove {p.name}</button>)}
      <button onClick={onRemove} className={`${chip} text-red-400`}>{parts ? 'Remove the circuit' : 'Remove'}</button>
    </div>
  );
}

const stripDone = (exercises = []) => exercises.map(ex => ({ name: ex.name, sets: ex.sets.map(s => [s.reps ?? null, s.weight ?? null, s.rest ?? 0]) }));

function Stepper({ label, value, step, onChange }) {
  const set = (v) => onChange(v == null ? null : Math.max(0, Math.round(v * 10) / 10));
  // From an in-between number (44.1 lb, converted from 20 kg) the first press
  // lands on the next whole step, not on another in-between one.
  const up = (v) => (Math.floor(v / step + 1e-9) + 1) * step;
  const down = (v) => (Math.ceil(v / step - 1e-9) - 1) * step;
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[#262626] p-1.5 text-center">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="flex items-center justify-between">
        <button onClick={() => set(down(value ?? 0))} className="w-9 h-9 text-xl text-gray-300">−</button>
        <span className="tabular-nums text-lg">{value ?? '-'}</span>
        <button onClick={() => set(up(value ?? 0))} className="w-9 h-9 text-xl text-gray-300">+</button>
      </div>
    </div>
  );
}

/** The rest timer: counts down from the tick and stops at zero. */
function RestBar({ rest, remaining, next, onNudge, onSkip }) {
  const { say } = useRabel();
  if (!rest) {
    if (!next) return null;
    return (
      <div className="fixed inset-x-0 bottom-0 bg-[#0a0a0a]/95 backdrop-blur border-t border-[#222]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="max-w-xl mx-auto px-4 py-3 text-sm text-gray-400">Next: <span className="text-white">{next.ex.name}</span>, {stepWord(next)} {next.n + 1} · {describeSet(next.s, unitOf(next.ex))}</div>
      </div>
    );
  }
  const over = remaining <= 0;
  const progress = rest.seconds ? Math.min(1, Math.max(0, 1 - remaining / (rest.seconds * 1000))) : 1;
  return (
    <div className={`fixed inset-x-0 bottom-0 border-t ${over ? 'bg-green-950/95 border-green-700' : 'bg-[#0a0a0a]/95 border-[#222]'} backdrop-blur`} style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="h-1 bg-[#1a1a1a]"><div className="h-full bg-green-500" style={{ width: `${progress * 100}%` }} /></div>
      <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className={`rabel-clock text-5xl font-bold tabular-nums leading-none ${over ? 'text-green-400' : 'text-white'}`}>{fmtClock(remaining)}</div>
          <div className="text-xs text-gray-400 mt-1 truncate">
            {over ? say('Rest over. ', 'Rest over, queen. Back out there 👑 ') : say('Rest. ', 'Breathe, babe 💗 ')}
            {next ? <>Next: <span className="text-gray-200">{next.ex.name}</span>, {stepWord(next)} {next.n + 1} · {describeSet(next.s, unitOf(next.ex))}</> : say('Last set done.', 'Last set done. Iconic.')}
          </div>
        </div>
        {!over && (
          <div className="flex gap-1.5">
            <button onClick={() => onNudge(-15)} className="w-12 h-12 rounded-xl bg-[#1a1a1a] text-sm">−15</button>
            <button onClick={() => onNudge(15)} className="w-12 h-12 rounded-xl bg-[#1a1a1a] text-sm">+15</button>
          </div>
        )}
        <button onClick={onSkip} className="h-12 px-3 rounded-xl bg-[#1a1a1a] text-sm text-gray-300">{over ? 'Close' : 'Skip'}</button>
      </div>
    </div>
  );
}

// --- History ------------------------------------------------------------------------------

function History() {
  const [runs, setRuns] = useState(null);
  useEffect(() => { gymAPI.runs().then(r => setRuns(r.data)).catch(() => setRuns([])); }, []);
  return (
    <>
      <Header title="History" back="/gym" />
      {runs === null && <p className="text-gray-500">Loading…</p>}
      {runs?.length === 0 && <p className="text-gray-500">No workouts finished yet.</p>}
      <div className="space-y-2">
        {runs?.map(r => {
          const minutes = Math.round((new Date(r.finished_at) - new Date(r.started_at)) / 60000);
          return (
            <Link key={r.id} to={`/gym/history/${r.id}`} className="block rounded-2xl bg-[#141414] border border-[#232323] px-4 py-3 active:bg-[#1a1a1a]">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold flex-1 truncate">{r.name}</span>
                <span className="text-xs text-gray-500">{new Date(r.finished_at).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              </div>
              <div className="text-sm text-gray-400">{r.sets_done} sets · {minutes} min <span className="text-gray-600">· tap to edit</span></div>
            </Link>
          );
        })}
      </div>
    </>
  );
}
